/**
 * Marketing Attribution Tracking for Contact Form 7
 * Captures traffic source, UTM params, device, journey time, IP/location (ipapi.co);
 * stores in localStorage; populates hidden field "traffic_details" before submit.
 * Uses navigator.userAgent for device (navigator.platform is deprecated).
 */

const STORAGE_KEYS = {
    first: 'attribution_first_visit',
    latest: 'attribution_latest_visit',
    journey: 'attribution_journey',
    journey_start_ts: 'attribution_journey_start_ts',
    journey_total_ms: 'attribution_journey_total_ms',
    page_enter_ts: 'attribution_page_enter_ts',
};

const JOURNEY_TTL_MS = 12 * 60 * 60 * 1000;
const IP_GEO_MAX_AGE_MS = 24 * 60 * 60 * 1000;
const TRAFFIC_FIELD_SELECTOR = 'input[name="traffic_details"], input#traffic_details';

let ipLoadListenerAdded = false;

function hostMatchesReferrerSource(domain, brandHost) {
    if (!domain || !brandHost) return false;
    return domain === brandHost || domain.slice(-(brandHost.length + 1)) === '.' + brandHost;
}

function getUrlParams() {
    let str = typeof window.location !== 'undefined' && window.location.search ? window.location.search : '';
    const params = {};
    if (str.charAt(0) === '?') str = str.slice(1);
    str.split('&').forEach(function (pair) {
        const i = pair.indexOf('=');
        if (i === -1) return;
        const k = decodeURIComponent(pair.slice(0, i).replace(/\+/g, ' '));
        const v = decodeURIComponent(pair.slice(i + 1).replace(/\+/g, ' '));
        params[k] = v;
    });
    return params;
}

function getDevice() {
    try {
        const ua = (navigator.userAgent || '').toLowerCase();
        if (/windows|win32|win64|wow64/.test(ua)) return 'Windows';
        if (/macintosh|mac os x/.test(ua)) return 'Mac';
        if (/android/.test(ua)) return 'Android';
        if (/linux/.test(ua)) return 'Linux';
        if (/iphone|ipad|ipod/.test(ua)) return 'iOS';
        return 'Unknown';
    } catch (e) {
        return 'Unknown';
    }
}

function getReferrer() {
    try {
        return document.referrer || '';
    } catch (e) {
        return '';
    }
}

function getCurrentHost() {
    try {
        return (window.location.hostname || '').toLowerCase();
    } catch (e) {
        return '';
    }
}

function referrerToSource(referrer) {
    if (!referrer) return 'Direct';
    let domain = '';
    try {
        const a = document.createElement('a');
        a.href = referrer;
        domain = (a.hostname || '').toLowerCase();
    } catch (e) {}
    const currentHost = getCurrentHost();
    if (currentHost && domain === currentHost) return 'Internal';
    if (hostMatchesReferrerSource(domain, 'google.com')) return 'Google Organic';
    if (hostMatchesReferrerSource(domain, 'bing.com')) return 'Bing Organic';
    if (hostMatchesReferrerSource(domain, 'linkedin.com')) return 'LinkedIn';
    if (hostMatchesReferrerSource(domain, 'facebook.com')) return 'Facebook';
    if (hostMatchesReferrerSource(domain, 'instagram.com')) return 'Instagram';
    if (hostMatchesReferrerSource(domain, 'twitter.com') || hostMatchesReferrerSource(domain, 'x.com')) return 'Twitter';
    const emailDomains = ['mail.', 'gmail.', 'yahoo.', 'outlook.', 'hotmail.', 'live.', 'icloud.', 'aol.', 'protonmail.', 'zoho.'];
    for (let i = 0; i < emailDomains.length; i++) {
        if (domain.indexOf(emailDomains[i]) !== -1) return 'Email';
    }
    return 'Referral';
}

function buildVisitSnapshot(params, referrer, landingPage, device) {
    const hasUtm = params.utm_source || params.utm_medium || params.utm_campaign || params.utm_term || params.utm_content;
    const hasClickId = !!(params.gclid || params.fbclid);
    let source = 'Direct';
    let medium = 'none';
    let campaign = 'none';
    let term = 'none';
    let content = 'none';

    if (params.gclid) {
        source = 'Google Ads';
        medium = 'cpc';
    } else if (params.fbclid) {
        source = 'Facebook Ads';
        medium = 'cpc';
    }

    if (hasUtm && !hasClickId) {
        if (params.utm_source) source = params.utm_source;
        if (params.utm_medium) medium = params.utm_medium;
        if (params.utm_campaign) campaign = params.utm_campaign;
        if (params.utm_term) term = params.utm_term;
        if (params.utm_content) content = params.utm_content;
    } else if (!hasClickId) {
        source = referrerToSource(referrer);
    }

    return {
        source,
        medium,
        campaign,
        term,
        content,
        landing_page: landingPage || '',
        referrer: referrer || '',
        device: device || 'Unknown',
        ip_address: 'Unknown',
        location: 'Unknown',
        ip_geo_fetched_at: 0,
        page_url: landingPage || '',
        browser: 'Unknown',
    };
}

function getPageUrl() {
    try {
        return window.location.href || '';
    } catch (e) {
        return '';
    }
}

function getBrowserName() {
    try {
        const ua = navigator.userAgent || '';
        if (/edg\//i.test(ua)) return 'Edge';
        if (/chrome\//i.test(ua) && !/edg\//i.test(ua)) return 'Chrome';
        if (/safari\//i.test(ua) && !/chrome\//i.test(ua)) return 'Safari';
        if (/firefox\//i.test(ua)) return 'Firefox';
        return 'Unknown';
    } catch (e) {
        return 'Unknown';
    }
}

function fetchIpLocation(timeoutMs) {
    return new Promise(function (resolve) {
        let done = false;
        function finish(data) {
            if (done) return;
            done = true;
            resolve(data || { ip_address: 'Unknown', location: 'Unknown' });
        }

        const timer = setTimeout(function () {
            finish({ ip_address: 'Unknown', location: 'Unknown' });
        }, timeoutMs || 5000);

        try {
            fetch('https://ipapi.co/json/', { method: 'GET' })
                .then(function (resp) {
                    return resp && resp.ok ? resp.json() : null;
                })
                .then(function (data) {
                    clearTimeout(timer);
                    if (!data) return finish();
                    const city = data.city || '';
                    const region = data.region || '';
                    const country = data.country_name || '';
                    const location = [city, region, country].filter(Boolean).join(', ') || 'Unknown';
                    finish({ ip_address: data.ip || 'Unknown', location });
                })
                .catch(function () {
                    clearTimeout(timer);
                    finish();
                });
        } catch (e) {
            clearTimeout(timer);
            finish();
        }
    });
}

function loadStored(key) {
    try {
        const raw = localStorage.getItem(key);
        return raw ? JSON.parse(raw) : null;
    } catch (e) {
        return null;
    }
}

function saveStored(key, data) {
    try {
        localStorage.setItem(key, JSON.stringify(data));
    } catch (e) {}
}

function loadNumberLegacy(key, fallback) {
    try {
        const raw = localStorage.getItem(key);
        if (raw == null) return fallback;
        const n = parseInt(raw, 10);
        return isNaN(n) ? fallback : n;
    } catch (e) {
        return fallback;
    }
}

function migrateLegacyJourneyIfNeeded() {
    try {
        if (localStorage.getItem(STORAGE_KEYS.journey)) return;
        const s = loadNumberLegacy(STORAGE_KEYS.journey_start_ts, 0);
        const t = loadNumberLegacy(STORAGE_KEYS.journey_total_ms, 0);
        const p = loadNumberLegacy(STORAGE_KEYS.page_enter_ts, 0);
        if (!s && !t && !p) return;
        saveJourneyState({ start_ts: s, total_ms: t, page_enter_ts: p });
        localStorage.removeItem(STORAGE_KEYS.journey_start_ts);
        localStorage.removeItem(STORAGE_KEYS.journey_total_ms);
        localStorage.removeItem(STORAGE_KEYS.page_enter_ts);
    } catch (e) {}
}

function loadJourneyState() {
    migrateLegacyJourneyIfNeeded();
    try {
        const raw = localStorage.getItem(STORAGE_KEYS.journey);
        if (!raw) return { start_ts: 0, total_ms: 0, page_enter_ts: 0 };
        const o = JSON.parse(raw);
        return {
            start_ts: parseInt(o.start_ts, 10) || 0,
            total_ms: parseInt(o.total_ms, 10) || 0,
            page_enter_ts: parseInt(o.page_enter_ts, 10) || 0,
        };
    } catch (e) {
        return { start_ts: 0, total_ms: 0, page_enter_ts: 0 };
    }
}

function saveJourneyState(state) {
    try {
        localStorage.setItem(STORAGE_KEYS.journey, JSON.stringify(state));
    } catch (e) {}
}

function resetJourneyTrackingState() {
    const now = Date.now();
    saveJourneyState({ start_ts: now, total_ms: 0, page_enter_ts: now });
}

function formatDurationMs(ms) {
    const totalSeconds = Math.max(0, Math.floor((ms || 0) / 1000));
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    if (mins > 0) return mins + 'm ' + secs + 's';
    return secs + 's';
}

function ensureJourneyState() {
    const now = Date.now();
    let j = loadJourneyState();
    if (!j.start_ts || now - j.start_ts > JOURNEY_TTL_MS) {
        resetJourneyTrackingState();
        return;
    }
    if (!j.page_enter_ts) j.page_enter_ts = now;
    if (j.total_ms == null || isNaN(j.total_ms)) j.total_ms = 0;
    saveJourneyState(j);
}

function syncJourneyTime() {
    const now = Date.now();
    const j = loadJourneyState();
    const enterTs = j.page_enter_ts || now;
    const delta = now - enterTs;
    if (delta > 0) j.total_ms = (j.total_ms || 0) + delta;
    j.page_enter_ts = now;
    saveJourneyState(j);
}

function getJourneyTotalMs() {
    const now = Date.now();
    const j = loadJourneyState();
    const enterTs = j.page_enter_ts || now;
    const delta = now - enterTs;
    let total = j.total_ms || 0;
    if (delta > 0) total += delta;
    return total;
}

function initJourneyTracking() {
    ensureJourneyState();
    document.addEventListener('visibilitychange', function () {
        if (document.visibilityState === 'hidden') {
            syncJourneyTime();
        } else if (document.visibilityState === 'visible') {
            const j = loadJourneyState();
            j.page_enter_ts = Date.now();
            saveJourneyState(j);
        }
    });
    window.addEventListener('pagehide', syncJourneyTime);
    window.addEventListener('beforeunload', syncJourneyTime);
    document.addEventListener('wpcf7mailsent', function () {
        resetJourneyTrackingState();
        try {
            localStorage.removeItem(STORAGE_KEYS.first);
            localStorage.removeItem(STORAGE_KEYS.latest);
        } catch (e) {}
    });
}

function formatTrafficDetailsString(first, latest) {
    const l = latest || {};
    const parts = [];
    function add(label, value) {
        if (value != null && value !== '' && value !== 'none') {
            parts.push(label + ': ' + value);
        }
    }
    add('Source', l.source);
    add('Medium', l.medium);
    add('Campaign', l.campaign);
    add('Term', l.term);
    add('Content', l.content);
    add('Device', l.device);
    add('IP', l.ip_address);
    add('Location', l.location);
    add('Page URL', l.page_url);
    add('Browser', l.browser);
    add('Time Spent', l.time_spent);
    return parts.join(' | ');
}

function runAttribution() {
    syncJourneyTime();
    const params = getUrlParams();
    const referrer = getReferrer();
    const landingPage =
        typeof window.location !== 'undefined'
            ? (window.location.origin || '') + (window.location.pathname || '') + (window.location.search || '')
            : '';
    const device = getDevice();
    const snapshot = buildVisitSnapshot(params, referrer, landingPage, device);
    const prevLatest = loadStored(STORAGE_KEYS.latest) || {};
    let first = loadStored(STORAGE_KEYS.first);

    snapshot.page_url = getPageUrl() || snapshot.page_url || '';
    snapshot.browser = getBrowserName();
    snapshot.time_spent = formatDurationMs(getJourneyTotalMs());

    if (first && first.landing_page) {
        snapshot.landing_page = first.landing_page;
    }

    if (prevLatest.ip_address) snapshot.ip_address = prevLatest.ip_address;
    if (prevLatest.location) snapshot.location = prevLatest.location;
    if (prevLatest.ip_geo_fetched_at) snapshot.ip_geo_fetched_at = prevLatest.ip_geo_fetched_at;

    if (!first) {
        saveStored(STORAGE_KEYS.first, snapshot);
        first = snapshot;
    }

    saveStored(STORAGE_KEYS.latest, snapshot);
    return { first, latest: snapshot };
}

function setTrafficDetailsInForms(value) {
    try {
        document.querySelectorAll(TRAFFIC_FIELD_SELECTOR).forEach(function (el) {
            if (el && el.tagName === 'INPUT') {
                el.value = value;
            }
        });
    } catch (e) {}
}

function onFormSubmit(e) {
    try {
        const form = e.target;
        if (!form || form.tagName !== 'FORM' || !form.querySelector || !form.querySelector(TRAFFIC_FIELD_SELECTOR)) return;
        syncJourneyTime();
        const data = runAttribution();
        const j = loadJourneyState();
        data.latest.time_spent = formatDurationMs(j.total_ms || 0);
        saveStored(STORAGE_KEYS.latest, data.latest);
        const str = formatTrafficDetailsString(data.first, data.latest);
        setTrafficDetailsInForms(str);
    } catch (err) {}
}

function delegatedFormSubmit(e) {
    const form = e.target;
    if (!form || form.tagName !== 'FORM') return;
    if (typeof form.className !== 'string' || form.className.indexOf('wpcf7-form') === -1) return;
    onFormSubmit(e);
}

function hasTrafficField() {
    try {
        return !!document.querySelector(TRAFFIC_FIELD_SELECTOR);
    } catch (e) {
        return false;
    }
}

function refreshTrafficDetailsIfPresent() {
    if (!hasTrafficField()) return;
    const data = runAttribution();
    setTrafficDetailsInForms(formatTrafficDetailsString(data.first, data.latest));
    scheduleEnrichment();
}

function scheduleEnrichment() {
    if (!hasTrafficField()) return;

    function runTask() {
        const latest = loadStored(STORAGE_KEYS.latest) || {};
        const now = Date.now();
        const fetchedAt = parseInt(latest.ip_geo_fetched_at, 10) || 0;
        const hasIp = latest.ip_address && latest.ip_address !== 'Unknown';
        if (hasIp && fetchedAt && now - fetchedAt < IP_GEO_MAX_AGE_MS) {
            setTrafficDetailsInForms(formatTrafficDetailsString(loadStored(STORAGE_KEYS.first), latest));
            return;
        }

        fetchIpLocation(5000).then(function (geo) {
            const lat = loadStored(STORAGE_KEYS.latest) || {};
            lat.ip_address = geo.ip_address || 'Unknown';
            lat.location = geo.location || 'Unknown';
            lat.ip_geo_fetched_at = Date.now();
            saveStored(STORAGE_KEYS.latest, lat);
            setTrafficDetailsInForms(formatTrafficDetailsString(loadStored(STORAGE_KEYS.first), lat));
        });
    }

    function idleRun() {
        if (typeof window.requestIdleCallback === 'function') {
            window.requestIdleCallback(runTask, { timeout: 8000 });
        } else {
            setTimeout(runTask, 3000);
        }
    }

    if (document.readyState === 'complete') {
        idleRun();
    } else if (!ipLoadListenerAdded) {
        ipLoadListenerAdded = true;
        window.addEventListener('load', idleRun);
    }
}

function attributionInit() {
    initJourneyTracking();
    document.addEventListener('submit', delegatedFormSubmit, true);
    document.addEventListener('wpcf7DOMContentLoaded', function () {
        refreshTrafficDetailsIfPresent();
    });
    refreshTrafficDetailsIfPresent();
}

export function initAttributionCF7() {
    attributionInit();
}
